import { useState, type FormEvent } from 'react';
import { createUser, roleLabel, type RoleRecord } from '@/services/users';

interface UserModalProps {
  roles: RoleRecord[];
  onClose: () => void;
  onCreated: () => void;
}

export default function UserModal({ roles, onClose, onCreated }: UserModalProps) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleId, setRoleId] = useState(roles[0]?.id ?? '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdPassword, setCreatedPassword] = useState('');
  const [passwordWasSet, setPasswordWasSet] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    if (!email.trim() || !fullName.trim() || !roleId) {
      setError('Completa el nombre, el correo y asigna un rol.');
      return;
    }
    if (password && password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (password && password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      const result = await createUser({
        email: email.trim(),
        fullName: fullName.trim(),
        roleId,
        password: password || undefined,
      });
      setCreatedPassword(result.tempPassword);
      setPasswordWasSet(result.passwordSet);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear el usuario.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md p-6 shadow-none border border-background-200">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground-950">Crear / Invitar usuario</h3>
            <p className="text-xs text-foreground-500 mt-0.5">Agrega un miembro al equipo y asígnale un rol.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line mt-0.5"></i>
            <span>{error}</span>
          </div>
        )}

        {createdPassword || passwordWasSet ? (
          <div className="space-y-4">
            <div className="flex items-start gap-2 text-sm bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg px-3 py-3">
              <i className="ri-checkbox-circle-line mt-0.5 text-emerald-600"></i>
              <div className="space-y-1">
                <p className="font-medium">Usuario creado correctamente.</p>
                {passwordWasSet ? (
                  <p className="text-emerald-700">
                    {fullName.trim() || 'El usuario'} ya puede iniciar sesión con la contraseña que definiste.
                  </p>
                ) : (
                  <>
                    <p className="text-emerald-700">
                      Comparte estas credenciales con {fullName.trim() || 'el usuario'}:
                    </p>
                    <p className="font-mono text-xs bg-white border border-emerald-200 rounded-md px-2 py-1.5">
                      {email.trim()} — Contraseña temporal: <strong>{createdPassword}</strong>
                    </p>
                    <p className="text-xs text-emerald-600">
                      Recomendale cambiarla tras el primer inicio de sesión.
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                Listo
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Nombre completo</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Ej: Laura Gómez"
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Correo electrónico</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@paticasconproposito.com"
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Rol</label>
              <select
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300 cursor-pointer"
              >
                {roles.length === 0 && <option value="">Sin roles disponibles</option>}
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {roleLabel(r)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Contraseña (opcional)</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Dejar vacío para generar una temporal"
                className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
              />
              <p className="text-[11px] text-foreground-400 mt-1">
                Si la defines, el usuario iniciará sesión con ella. Mínimo 6 caracteres.
              </p>
            </div>

            {password && (
              <div>
                <label className="text-xs font-medium text-foreground-600 mb-1.5 block">Confirmar contraseña</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repite la contraseña"
                  className="w-full text-sm border border-background-300 rounded-lg px-3 py-2.5 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
                />
              </div>
            )}

            <div className="pt-2 flex gap-3 justify-end">
              <button
                type="button"
                onClick={onClose}
                className="text-sm px-4 py-2.5 rounded-lg border border-background-200 text-foreground-700 hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="text-sm px-5 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-60 whitespace-nowrap font-medium cursor-pointer"
              >
                {saving ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}